import React, { memo } from "react";
import { Resource } from "../../types/resources";
import { TimeSlot, ViewConfig, SchedulerTheme } from "../../types/views";
import GridCell from "./GridCell";
import { useDroppable } from "@dnd-kit/core";

interface GridRowProps {
  resource: Resource;
  startDate: Date;
  daysToShow: number;
  cellWidth: number;
  cellHeight: number;
  top: number;
  onCellClick?: (slot: TimeSlot) => void;
  onCellHover?: (slot: TimeSlot | null, event?: React.MouseEvent) => void;
  viewConfig?: ViewConfig;
  isDraggable?: boolean;
  schedulerTheme?: SchedulerTheme;
}

const GridRow: React.FC<GridRowProps> = memo(
  ({
    resource,
    startDate,
    daysToShow,
    cellWidth,
    cellHeight,
    top,
    onCellClick,
    onCellHover,
    viewConfig,
    isDraggable = false,
    schedulerTheme,
  }) => {
    const { setNodeRef, isOver } = useDroppable({
      id: resource.id,
      data: { resource: resource, type: "resource-row" },
      disabled: !isDraggable,
    });

    const renderCells = () => {
      const cells = [];
      const minutesPerCell = 30;
      const cellsPerDay = 48; // 24 hours * 2 (30-minute intervals)

      for (let day = 0; day < daysToShow; day++) {
        for (let cell = 0; cell < cellsPerDay; cell++) {
          const currentDate = new Date(startDate);
          currentDate.setDate(currentDate.getDate() + day);
          currentDate.setHours(Math.floor(cell / 2));
          currentDate.setMinutes((cell % 2) * 30);

          const endDate = new Date(currentDate);
          endDate.setMinutes(endDate.getMinutes() + minutesPerCell);

          const hour = currentDate.getHours();
          const isWeekend =
            currentDate.getDay() === 0 || currentDate.getDay() === 6;
          // Use work hours from: 1) viewConfig, 2) dispatch config, 3) default fallback
          const configWorkHours =
            viewConfig?.workHours ||
            schedulerTheme?.getWorkHours?.(viewConfig) || { start: 9, end: 17 };
          const isWorkHour =
            hour >= configWorkHours.start && hour < configWorkHours.end;

          const timeSlot: TimeSlot = {
            start: currentDate,
            end: endDate,
            resources: [resource],
            events: [],
            isWorkHour,
            isWeekend,
          };

          cells.push(
            <GridCell
              key={`${resource.id}-${day}-${cell}`}
              timeSlot={timeSlot}
              width={cellWidth}
              height={cellHeight}
              onClick={() => onCellClick?.(timeSlot)}
              onHover={(e) => onCellHover?.(timeSlot, e)}
              onHoverEnd={() => onCellHover?.(null)}
              schedulerTheme={schedulerTheme}
            />
          );
        }
      }
      return cells;
    };

    return (
      <div
        ref={setNodeRef}
        className="grid-row"
        style={{
          display: "flex",
          position: "absolute",
          top,
          left: 0,
          right: 0,
          height: cellHeight,
          borderBottom: "1px solid #e0e0e0",
          backgroundColor: isOver
            ? schedulerTheme?.eventDropTargetColor || "#f0f8ff"
            : "transparent",
          transition: "background-color 0.2s ease-in-out",
        }}
      >
        {renderCells()}
      </div>
    );
  }
);

GridRow.displayName = "GridRow";

export default GridRow;
